/**
 * Pre-dither source image adjustments. Pure function - takes a
 * grayscale ImageData (R = G = B; alpha intact) and returns a new
 * ImageData with brightness, contrast, and gamma applied in that
 * order.
 *
 * Used by the demo to keep `state.sourceOriginal` immutable: each
 * slider change re-runs `applySourceAdjust(sourceOriginal, …)` to
 * produce a fresh `state.source` that the dither pipeline consumes.
 *
 * Defaults (brightness 0, contrast 0, gamma 1) are an identity:
 * the function returns a copy that should byte-equal the input apart
 * from rounding stability, so the "no adjustment" path stays cheap.
 */
export interface SourceAdjustParams {
  /** -100..100. Linear add to the [0, 1] gray, scaled by /100. Default 0. */
  brightness: number;
  /** -100..100. Multiplier around 0.5: `(v - 0.5) * (1 + contrast/100) + 0.5`. Default 0. */
  contrast: number;
  /** 0.1..3.0. Output gamma: `pow(v, 1/gamma)`. <1 darkens mids, >1 brightens. Default 1. */
  gamma: number;
}

export function applySourceAdjust(src: ImageData, params: SourceAdjustParams): ImageData {
  const w = src.width;
  const h = src.height;
  const out = new ImageData(w, h);
  const sd = src.data;
  const dd = out.data;

  const brightnessShift = params.brightness / 100;
  const contrastFactor = 1 + params.contrast / 100;
  const gammaSafe = Math.max(0.01, params.gamma);
  const invGamma = 1 / gammaSafe;
  const isIdentity =
    brightnessShift === 0 && contrastFactor === 1 && gammaSafe === 1;

  for (let i = 0; i < sd.length; i += 4) {
    let v = (sd[i] ?? 0) / 255;
    if (!isIdentity) {
      v = (v - 0.5) * contrastFactor + 0.5;
      v = v + brightnessShift;
      if (v < 0) v = 0;
      else if (v > 1) v = 1;
      if (gammaSafe !== 1) v = Math.pow(v, invGamma);
      if (v < 0) v = 0;
      else if (v > 1) v = 1;
    }
    const byte = Math.round(v * 255);
    dd[i] = byte;
    dd[i + 1] = byte;
    dd[i + 2] = byte;
    dd[i + 3] = sd[i + 3] ?? 255;
  }

  return out;
}
